/** Zona 4 (telas L-03 e L-05): destaques ou tutor, alternados.
 *
 * Acessibilidade que o painel precisa e que raramente vem de graça: ao abrir, o
 * foco vai para o título (senão quem navega por teclado continua no botão que
 * abriu e não percebe que algo mudou); `Escape` fecha e DEVOLVE o foco para
 * aquele botão.
 */
import { useEffect, useRef } from 'react'

import { TutorPanel } from '@/features/tutor/TutorPanel'

import { useReaderStore } from '@/features/reader/stores/readerStore'

const ROTULOS = {
  destaques: 'Anotações e destaques',
  tutor: 'Assistente Tutor',
  ferramentas: 'Busca e favoritos',
} as const

interface RightPanelProps {
  /** Conteúdo do painel de DESTAQUES; o Tutor usa o livro aberto. */
  children?: React.ReactNode
  ferramentas?: React.ReactNode
  searchTargetRef?: (element: HTMLDivElement | null) => void
}

export function RightPanel({ children, ferramentas, searchTargetRef }: RightPanelProps) {
  const bookId = useReaderStore((s) => s.bookId)
  const painel = useReaderStore((s) => s.painelDireito)
  const abrirPainel = useReaderStore((s) => s.abrirPainel)
  const tituloRef = useRef<HTMLHeadingElement>(null)
  const focoAnterior = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (painel === 'nenhum') return
    focoAnterior.current = document.activeElement as HTMLElement | null
    tituloRef.current?.focus()

    const onKeyDown = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        abrirPainel('nenhum')
        focoAnterior.current?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [painel, abrirPainel])

  const label = painel === 'nenhum' ? 'Busca e favoritos' : ROTULOS[painel]

  return (
    <aside className={`reader-rightpanel${painel === 'ferramentas' ? ' reader-rightpanel--study' : ''}`} aria-label={label} hidden={painel === 'nenhum'}>
      <div className="reader-rightpanel__cabecalho">
        <h2 ref={tituloRef} tabIndex={-1}>
          {label}
        </h2>
        <button
          type="button"
          onClick={() => {
            abrirPainel('nenhum')
            focoAnterior.current?.focus()
          }}
          aria-label={`Fechar ${label}`}
        >
          ✕
        </button>
      </div>
      <div id="reader-study-panel" hidden={painel !== 'ferramentas'}>
        {ferramentas}
        <div ref={searchTargetRef} />
      </div>
      {painel === 'ferramentas' || painel === 'nenhum' ? null : painel === 'tutor' && bookId ? <TutorPanel key={bookId} bookId={bookId} /> : painel === 'destaques' && children ? (
        children
      ) : (
        <p className="reader-rightpanel__vazio" role="status">
          {painel === 'tutor'
            ? 'O assistente tutor chega numa próxima etapa.'
            : 'Selecione um trecho do livro para criar seu primeiro destaque.'}
        </p>
      )}
    </aside>
  )
}
